import type { NatureStageSnapshot } from "./stageState";

declare const __NATURE_MODEL_REVISION__: string;

const MODEL_BUDGET_BYTES = 2 * 1024 * 1024;

export type SceneModelId = "phenology" | "latitude" | "longitude" | "elevation" | "era";
export type LoadedSceneModelId = SceneModelId | "common";
export type SceneModelFile = `${LoadedSceneModelId}-stage.glb` | "china-map.glb";
export type VersionedSceneModelUrl = `/media/v2/models/${SceneModelFile}?v=${string}`;

export interface SceneModelFraming {
  readonly cameraPosition: readonly [number, number, number];
  readonly cameraTarget: readonly [number, number, number];
  readonly compactCameraPosition: readonly [number, number, number];
  readonly compactCameraTarget: readonly [number, number, number];
}

export const modelAssetRevision = typeof __NATURE_MODEL_REVISION__ === "string"
  ? __NATURE_MODEL_REVISION__
  : "development";

export function getVersionedModelUrl(file: SceneModelFile): VersionedSceneModelUrl {
  return `/media/v2/models/${file}?v=${modelAssetRevision}`;
}

export interface SceneModelDefinition<Id extends string = string> {
  readonly id: Id;
  readonly url: VersionedSceneModelUrl;
  readonly budgetBytes: number;
  readonly requiredNodes: readonly string[];
  readonly framing: SceneModelFraming;
}

export const commonStageModel = {
  id: "common",
  url: getVersionedModelUrl("common-stage.glb"),
  budgetBytes: MODEL_BUDGET_BYTES,
  framing: {
    cameraPosition: [0, 2.6, 8.8],
    cameraTarget: [0, -0.15, 0],
    compactCameraPosition: [0, 2.75, 9.2],
    compactCameraTarget: [0, -0.75, 0]
  },
  requiredNodes: [
    "ROOT_CommonStage",
    "COMMON_River",
    "COMMON_Mountain_Back",
    "COMMON_Mountain_Mid",
    "COMMON_Mountain_Right"
  ]
} as const satisfies SceneModelDefinition<"common">;

export const chinaMapModel = {
  id: "china",
  url: getVersionedModelUrl("china-map.glb"),
  budgetBytes: MODEL_BUDGET_BYTES,
  framing: {
    cameraPosition: [-0.48, 2.25, 8.1],
    cameraTarget: [-0.48, -0.08, 0],
    compactCameraPosition: [0, 2.65, 11],
    compactCameraTarget: [0, -1.05, 0]
  },
  requiredNodes: ["ROOT_ChinaMap", "MESH_ChinaOutline", "MESH_Taiwan", "MESH_Hainan"]
} as const satisfies SceneModelDefinition<"china">;

export const sceneModelCatalog = {
  phenology: {
    id: "phenology",
    url: getVersionedModelUrl("phenology-stage.glb"),
    budgetBytes: MODEL_BUDGET_BYTES,
    framing: {
      cameraPosition: [-0.48, 2.15, 7.7],
      cameraTarget: [-0.48, -0.08, 0],
    compactCameraPosition: [0, 2.55, 10.8],
      compactCameraTarget: [0, -1.05, 0]
    },
    requiredNodes: [
      "ROOT_LivingInstrument",
      "C2_FOCUS_PEACH",
      "C2_FOCUS_BIRD",
      "C2_FOCUS_INSECT",
      "C2_FOCUS_WEATHER",
      "C2_STAGE_DORMANT",
      "C2_STAGE_BUDS",
      "C2_STAGE_BLOSSOMS",
      "C2_STAGE_LEAVES",
      "C2_WindVane",
      "C2_AnemometerRotor"
    ]
  },
  latitude: {
    id: "latitude",
    url: getVersionedModelUrl("latitude-stage.glb"),
    budgetBytes: MODEL_BUDGET_BYTES,
    // latitude 场景 group 放大 2.4375 倍；城市中点世界坐标约
    // desktop (2.88, -1.15, -0.04) / compact (1.68, -1.28, -0.02)。
    // 相机放在 +Z 侧（地理南侧），北朝上、南朝下；俯仰约 45°（略偏平视）。
    framing: {
      cameraPosition: [2.88, 2.17, 3.28],
      cameraTarget: [2.88, -1.15, -0.04],
      compactCameraPosition: [1.68, 2.24, 3.38],
      compactCameraTarget: [1.68, -1.28, -0.02]
    },
    requiredNodes: [
      "ROOT_Latitude",
      "C3_LAT_NANJING",
      "C3_LAT_BEIJING",
      "C3_LAT_NANJING_EARLY",
      "C3_LAT_NANJING_LATE",
      "C3_LAT_BEIJING_EARLY",
      "C3_LAT_BEIJING_LATE",
      "C3_LAT_FACTOR_MARKER"
    ]
  },
  longitude: {
    id: "longitude",
    url: getVersionedModelUrl("longitude-stage.glb"),
    budgetBytes: MODEL_BUDGET_BYTES,
    // longitude group 已放大 4.1 倍并平移到渤海湾中点；城市缩小后略拉远相机以保留半岛。
    framing: {
      cameraPosition: [-0.48, 0.62, 4.45],
      cameraTarget: [-0.48, -1.02, 0],
      compactCameraPosition: [-0.48, 0.78, 5.65],
      compactCameraTarget: [-0.48, -1.12, 0]
    },
    requiredNodes: [
      "ROOT_CoastInland",
      "C3_LONG_INLAND",
      "C3_LONG_COAST",
      "C3_CoastWater",
      "C3_LONG_FACTOR_MARKER"
    ]
  },
  elevation: {
    id: "elevation",
    url: getVersionedModelUrl("elevation-stage.glb"),
    budgetBytes: MODEL_BUDGET_BYTES,
    framing: {
      cameraPosition: [-0.35, 2.9, 8.5],
      cameraTarget: [-0.35, -0.02, 0],
    compactCameraPosition: [0.2, 3.15, 11.2],
      compactCameraTarget: [0, -1.05, 0]
    },
    requiredNodes: [
      "ROOT_Altitude",
      "C3_ELEV_BASE",
      "C3_ELEV_MIDDLE",
      "C3_ELEV_TOP",
      "C3_ELEV_SPRING",
      "C3_ELEV_AUTUMN",
      "C3_ELEV_INVERSION",
      "C3_ELEV_FACTOR_MARKER"
    ]
  },
  era: {
    id: "era",
    url: getVersionedModelUrl("era-stage.glb"),
    budgetBytes: MODEL_BUDGET_BYTES,
    framing: {
      cameraPosition: [-0.48, 2.25, 8.1],
      cameraTarget: [-0.48, -0.08, 0],
    compactCameraPosition: [0, 2.65, 11],
      compactCameraTarget: [0, -1.05, 0]
    },
    requiredNodes: [
      "ROOT_TimeCompare",
      "C3_ERA_EARLY",
      "C3_ERA_MODERN"
    ]
  }
} as const satisfies Record<SceneModelId, SceneModelDefinition<SceneModelId>>;

export function getSceneModelId(snapshot: NatureStageSnapshot): SceneModelId | null {
  if (snapshot.mode === "phenology") return "phenology";
  if (snapshot.mode === "factor") return snapshot.factorId;
  return null;
}

export function sceneUsesChinaMap(
  modelId: LoadedSceneModelId | null
): modelId is "latitude" | "longitude" {
  return modelId === "latitude" || modelId === "longitude";
}

export function sceneUsesCommonStage(modelId: LoadedSceneModelId | null): boolean {
  return !sceneUsesChinaMap(modelId);
}

export const nextStageModelId: Partial<Record<LoadedSceneModelId, LoadedSceneModelId>> = {
  latitude: "longitude",
  longitude: "elevation",
  elevation: "era"
};
